import { TenantBaseApi } from "api/TenantSlug/TenantBaseApi";

import GetPerformanceReportRequest = App.Http.Requests.Report.GetPerformanceReportRequest;
import GetPerformanceReportResponse = App.Http.Responses.Report.GetPerformanceReportResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import GetCascadeReportRequest = App.Http.Requests.Report.GetCascadeReportRequest;
import GetCascadeReportResponse = App.Http.Responses.Report.GetCascadeReportResponse;
import GetCascadeReportExportRequest = App.Http.Requests.Report.GetCascadeReportExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;

export interface GetCascadeReportGraphResponse {
    timeline: Record<string, Record<string, GetCascadeReportResponse["series"][number]>>;
}

export interface GetAffiliateReportGraphResponse {
    timeline: Record<string, Record<string, GetPerformanceReportResponse["series"][number]>>;
}

export interface GetAdvertiserReportGraphResponse {
    timeline: Record<string, Record<string, GetPerformanceReportResponse["series"][number]>>;
}

export interface GetOfferReportGraphResponse {
    timeline: Record<string, Record<string, GetPerformanceReportResponse["series"][number]>>;
}

export interface GetPerformanceGraphResponse {
    timeline: Record<string, GetPerformanceReportResponse["series"][number]>;
}

class ReportApi extends TenantBaseApi {
    /**
     * Get performance report
     */
    async getPerformance(request: GetPerformanceReportRequest): Promise<GetPerformanceReportResponse> {
        return await this.get<GetPerformanceReportResponse, GetPerformanceReportRequest>("/reports/performance", {
            params: request,
        });
    }

    async getPerformanceGraph(request: GetPerformanceReportRequest): Promise<GetPerformanceGraphResponse> {
        return await this.get<GetPerformanceGraphResponse, GetPerformanceReportRequest>("/reports/performance-graph", {
            params: request,
        });
    }

    /**
     * Export performance report
     */
    async exportPerformance(request: BaseExportRequest): Promise<ExportResponse> {
        return await this.post<ExportResponse, BaseExportRequest>("/reports/performance/export", request);
    }

    /**
     * Get performance offers report
     */
    async getPerformanceOffers(request: GetPerformanceReportRequest): Promise<GetPerformanceReportResponse> {
        return await this.get<GetPerformanceReportResponse, GetPerformanceReportRequest>(
            "/reports/performance-offers",
            { params: request }
        );
    }

    async getPerformanceOffersGraph(request: GetPerformanceReportRequest): Promise<GetOfferReportGraphResponse> {
        return await this.get<GetOfferReportGraphResponse, GetPerformanceReportRequest>(
            "/reports/performance-offers-graph",
            {
                params: request,
            }
        );
    }

    /**
     * Export performance offers report
     */
    async exportPerformanceOffers(request: BaseExportRequest): Promise<ExportResponse> {
        return await this.post<ExportResponse, BaseExportRequest>("/reports/performance-offers/export", request);
    }

    /**
     * Get performance affiliates report
     */
    async getPerformanceAffiliates(request: GetPerformanceReportRequest): Promise<GetPerformanceReportResponse> {
        return await this.get<GetPerformanceReportResponse, GetPerformanceReportRequest>(
            "/reports/performance-affiliates",
            { params: request }
        );
    }

    async getPerformanceAffiliatesGraph(
        request: GetPerformanceReportRequest
    ): Promise<GetAffiliateReportGraphResponse> {
        return await this.get<GetAffiliateReportGraphResponse, GetPerformanceReportRequest>(
            "/reports/performance-affiliates-graph",
            {
                params: request,
            }
        );
    }

    /**
     * Export performance affiliates report
     */
    async exportPerformanceAffiliates(request: BaseExportRequest): Promise<ExportResponse> {
        return await this.post<ExportResponse, BaseExportRequest>("/reports/performance-affiliates/export", request);
    }

    /**
     * Get performance advertisers report
     */
    async getPerformanceAdvertisers(request: GetPerformanceReportRequest): Promise<GetPerformanceReportResponse> {
        return await this.get<GetPerformanceReportResponse, GetPerformanceReportRequest>(
            "/reports/performance-advertiser",
            { params: request }
        );
    }

    async getPerformanceAdvertisersGraph(
        request: GetPerformanceReportRequest
    ): Promise<GetAdvertiserReportGraphResponse> {
        return await this.get<GetAdvertiserReportGraphResponse, GetPerformanceReportRequest>(
            "/reports/performance-advertiser-graph",
            {
                params: request,
            }
        );
    }

    /**
     * Export performance advertisers report
     */
    async exportPerformanceAdvertisers(request: BaseExportRequest): Promise<ExportResponse> {
        return await this.post<ExportResponse, BaseExportRequest>("/reports/performance-advertiser/export", request);
    }

    /**
     * Get cascades report
     */
    async getCascades(request: GetCascadeReportRequest): Promise<GetCascadeReportResponse> {
        return await this.get<GetCascadeReportResponse, GetCascadeReportRequest>("/reports/cascades", {
            params: request,
        });
    }

    async getCascadesGraph(request: GetCascadeReportRequest): Promise<GetCascadeReportGraphResponse> {
        return await this.get<GetCascadeReportGraphResponse, GetCascadeReportRequest>("/reports/cascades-graph", {
            params: request,
        });
    }

    async getCascadeGraph(id: string, request: GetCascadeReportRequest): Promise<GetCascadeReportGraphResponse> {
        return await this.get<GetCascadeReportGraphResponse, GetCascadeReportRequest>(`/reports/cascades-graph/${id}`, {
            params: request,
        });
    }

    /**
     * Export cascades report
     */
    async exportCascades(request: GetCascadeReportExportRequest): Promise<ExportResponse> {
        return await this.post<ExportResponse, GetCascadeReportExportRequest>("/reports/cascades/export", request);
    }

    /**
     * Get cascade channels report
     */
    async getCascadeChannels(cascadeId: string, request: GetCascadeReportRequest): Promise<GetCascadeReportResponse> {
        return await this.get<GetCascadeReportResponse, GetCascadeReportRequest>(`/reports/cascades/${cascadeId}`, {
            params: request,
        });
    }

    /**
     * Export cascade channels report
     */
    async exportCascadeChannels(cascadeId: string, request: GetCascadeReportExportRequest): Promise<ExportResponse> {
        return await this.post<ExportResponse, GetCascadeReportExportRequest>(
            `/reports/cascades/${cascadeId}/export`,
            request
        );
    }
}

export const reportApi = new ReportApi();
